import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { PropertyFilterProps } from "@amzn/awsui-components-react";
import { BulkOrderSource } from "components/BulkOrdersTemplates/interfaces";
import { BulkOrdersTemplatesService } from "services/bulk-orders-templates-service";
import { usePreferences } from "hooks/usePreferences";
import { ColumnSetting } from "common/utils/config-models";
import { defaultPagingOptions } from "common/utils/table-utils";
import { convertTokensToPropertyFilter } from "common/utils/property-filter-helper";
import tableConfig from "components/BulkOrdersTemplates/bulk-orders-templates.table-config.json";

const columns: ColumnSetting[] = tableConfig.columns as ColumnSetting[];

export interface UseBulkOrdersTemplatesProps {
    preferencesKey: string;
    vendor?: string;
    status?: boolean;
    defaultPageSize?: number;
    onError?: (error: unknown) => void;
}

export interface UseBulkOrdersTemplatesResult {
    items: BulkOrderSource[];
    loading: boolean;
    currentPage: number;
    totalPages: number;
    filterQuery: PropertyFilterProps.Query;
    setFilterQuery: (query: PropertyFilterProps.Query) => void;
    setCurrentPage: (page: number) => void;
    refresh: () => void;
    currentPreferences: ReturnType<typeof usePreferences>["currentPreferences"];
    onPreferencesConfirmHandler: ReturnType<typeof usePreferences>["onPreferencesConfirmHandler"];
    columns: ColumnSetting[];
}

export function useBulkOrdersTemplates({
    preferencesKey,
    vendor,
    status,
    defaultPageSize,
    onError,
}: UseBulkOrdersTemplatesProps): UseBulkOrdersTemplatesResult {
    const [items, setItems] = useState<BulkOrderSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: "and" });
    const [currentPage, setCurrentPage] = useState(1);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const tokenHistoryRef = useRef<(string | undefined)[]>([undefined]);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey,
        refreshData: () => {},
        columnsSettings: columns,
        defaultPaging: defaultPageSize || defaultPagingOptions[0].value,
    });

    const fetchIdRef = useRef(0);

    const keyedFilters = useMemo(() => convertTokensToPropertyFilter(filterQuery.tokens), [filterQuery]);

    const doFetch = useCallback(
        (page: number) => {
            const id = ++fetchIdRef.current;
            const token = tokenHistoryRef.current[page - 1];

            setLoading(true);
            BulkOrdersTemplatesService.instance
                .getSources({
                    vendor: vendor || undefined,
                    status,
                    limit: currentPreferences.pageSize!,
                    nextToken: token,
                    filters: keyedFilters,
                })
                .then((data) => {
                    if (fetchIdRef.current === id) {
                        setItems(data.items);
                        setNextToken(data.nextToken ?? null);
                        if (data.nextToken && !tokenHistoryRef.current[page]) {
                            tokenHistoryRef.current[page] = data.nextToken;
                        }
                    }
                })
                .catch((error) => {
                    if (fetchIdRef.current === id) {
                        setItems([]);
                        onErrorRef.current?.(error);
                    }
                })
                .finally(() => {
                    if (fetchIdRef.current === id) {
                        setLoading(false);
                    }
                });
        },
        [vendor, status, keyedFilters, currentPreferences.pageSize],
    );

    useEffect(() => {
        setCurrentPage(1);
        tokenHistoryRef.current = [undefined];
        doFetch(1);
    }, [doFetch]);

    const refresh = useCallback(() => {
        tokenHistoryRef.current = [undefined];
        setNextToken(null);
        setCurrentPage(1);
        doFetch(1);
    }, [doFetch]);

    const handlePageChange = useCallback(
        (page: number) => {
            setCurrentPage(page);
            doFetch(page);
        },
        [doFetch],
    );

    const totalPages = nextToken ? currentPage + 1 : currentPage;

    return {
        items,
        loading,
        currentPage,
        totalPages,
        filterQuery,
        setFilterQuery,
        setCurrentPage: handlePageChange,
        refresh,
        currentPreferences,
        onPreferencesConfirmHandler,
        columns,
    };
}
